import { motion } from 'framer-motion';
import { useTranscricao } from '../../context/TranscricaoContext';
import './CompletingWord.css'; // Reutiliza CSS existente

/**
 * Renderiza uma palavra individual com status de pronúncia
 * @param {Object} props
 * @param {Object} props.wordObj - Objeto com { word, status, confidence, attempts, transcribedWord, block }
 * @param {Function} props.onWordClick - Callback ao clicar
 */
export default function WordWithStatus({ wordObj, onWordClick }) {
  const { word, status: pronunciationStatus } = wordObj;
  const { transcriptionHistory } = useTranscricao();

  // Cor do fill - simples verificação da palavra para placeholder
  const getFillColor = () => {
    const wordLower = word.toLowerCase();
    if (wordLower === 'on' || wordLower === 'i' || wordLower === 'like' || wordLower === 'to') {
      return '#4CAF50'; // verde
    }
    if (wordLower === 'mondays') {
      return '#F44336'; // vermelho
    }
    return '#64898D'; // padrão
  };

  // Pega a última frase completa do histórico
  const lastTranscription = transcriptionHistory.length > 0
    ? transcriptionHistory[transcriptionHistory.length - 1].text
    : null;

  const handleClick = () => {
    if (onWordClick) {
      onWordClick(word);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Determina a classe CSS baseada no status de pronúncia
  const getStatusClass = () => {
    switch (pronunciationStatus) {
      case 'correct':
        return 'pronunciation-correct';
      case 'auto-approved':
        return 'pronunciation-auto-approved';
      case 'incorrect':
        return 'pronunciation-incorrect';
      case 'spoken':
        return 'pronunciation-spoken';
      case 'pending':
      default:
        return 'pronunciation-pending';
    }
  };


  return (
    <span
      className={`completing-word ${getStatusClass()}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Palavra: ${word}. Status: ${pronunciationStatus}. Clique para marcar como completada.`}
      style={{ position: 'relative', display: 'inline-block', margin: 0, padding: 0 }}
    >
      {/* Transcrição completa acima (só quando errada) */}
      {pronunciationStatus === 'incorrect' && lastTranscription && (
        <motion.span
          key={lastTranscription}
          style={{
            position: 'absolute',
            top: '-24px',
            left: '0',
            fontSize: '2rem',
            color: 'rgba(0, 0, 0, 0.4)',
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'normal',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            textShadow: 'none',
            pointerEvents: 'none'
          }}
          initial={{ y: 0, opacity: 1 }}
          animate={{ y: -40, opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          {lastTranscription.replace(/[^\w\s]/g, '')}
        </motion.span>
      )}

      {/* Renderiza palavra em SVG */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="36"
        viewBox={`0 0 ${word.length * 26} 36`}
        style={{
          display: 'inline-block',
          verticalAlign: 'baseline',
          marginRight: '4px',
          overflow: 'visible'
        }}
      >
        {/* GRUPO DA SOMBRA */}
        <g transform="translate(8, 8)">
          <text
            x="0"
            y="29"
            fontSize="36"
            fontFamily="Paytone One, Arial, sans-serif"
            fontWeight="1000"
            fill="rgba(0,0,0,0.35)"
          >
            {word}
          </text>
        </g>

        {/* GRUPO PRINCIPAL */}
        <g>
          <text
            x="0"
            y="29"
            fontSize="36"
            fontFamily="Paytone One, Arial, sans-serif"
            fontWeight="1000"
            fill="none"
            stroke="rgba(0,0,0,0.5)"
            strokeWidth="8"
            strokeLinejoin="round"
          >
            {word}
          </text>

          <text
            x="0"
            y="29"
            fontSize="36"
            fontFamily="Paytone One, Arial, sans-serif"
            fontWeight="1000"
            fill="none"
            stroke="#fff"
            strokeWidth="5"
            strokeLinejoin="round"
          >
            {word}
          </text>

          <text
            x="0"
            y="29"
            fontSize="36"
            fontFamily="Paytone One, Arial, sans-serif"
            fontWeight="1000"
            fill={getFillColor()}
          >
            {word}
          </text>
        </g>
      </svg>
    </span>
  );
}

